import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import AdminPage from '../../admin/shared/AdminPage'
import Loading from '../../../molecules/Loading'
import SydButton from '../../../commonDesign/Button'
import SydInput from '../../../commonDesign/SydInput'
import SydSelect from '../../../commonDesign/SydSelect'
import SydLabel, { hookFormErrorAdapter } from '../../../commonDesign/SydLabel'
import Card from '../../../molecules/Card'
import FlexRow from '../../../molecules/FlexRow'
import FlexActions from '../../../molecules/FlexActions'
import {
  useGetManagedSchedule,
  useListScheduleTargets,
  useCreateManagedSchedule,
  useModifyManagedSchedule
} from '../../../../api/scheduler'
import { useListDelegatedIdentities } from '../../../../api/delegatedIdentities'
import SydCheckbox from '../../../commonDesign/SydCheckbox'
import DynamicParameterFields from './DynamicParameterFields'
import CronBuilder from './CronBuilder'

const RETRY_POLICIES = [
  { label: 'Default (5 retries, 1hr window)', value: 'default' },
  { label: 'No Retry', value: 'no-retry' },
  { label: 'Aggressive (10 retries, 24hr window)', value: 'aggressive' }
]

const TIMEZONE_OPTIONS = [
  { label: 'America/New_York', value: 'America/New_York' },
  { label: 'America/Chicago', value: 'America/Chicago' },
  { label: 'America/Denver', value: 'America/Denver' },
  { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
  { label: 'UTC', value: 'UTC' }
]

const ACTION_OPTIONS = [
  { label: 'None', value: 'NONE' },
  { label: 'Delete', value: 'DELETE' }
]

const CODE_PATTERN = /^[a-z0-9][a-z0-9-]*$/

const DEFAULT_VALUES = {
  name: '',
  code: '',
  description: '',
  expression: '',
  timezone: 'America/New_York',
  targetType: 'command',
  targetCode: '',
  input: {},
  retryPolicyName: 'default',
  enabled: true,
  startDate: '',
  endDate: '',
  actionAfterCompletion: 'NONE',
  delegatedIdentityCode: '',
  captureResponse: false
}

function ScheduleForm () {
  const { scheduleId } = useParams()
  const isEdit = !!scheduleId
  const history = useHistory()
  const { url } = useRouteMatch()
  const managedBase = url.replace(/\/(new|[^/]+\/edit)$/, '')

  const { data: existingSchedule, isLoading: isLoadingSchedule } = useGetManagedSchedule(
    isEdit ? Number(scheduleId) : null
  )
  const { data: targetGroups, isLoading: isLoadingTargets } = useListScheduleTargets()
  const { data: delegatedIdentities } = useListDelegatedIdentities()
  const { mutateAsync: createSchedule } = useCreateManagedSchedule()
  const { mutateAsync: modifySchedule } = useModifyManagedSchedule()

  const [processing, setProcessing] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  })

  useEffect(() => {
    if (isEdit && existingSchedule) {
      form.reset({
        name: existingSchedule.name || '',
        code: existingSchedule.code || '',
        description: existingSchedule.description || '',
        expression: existingSchedule.expression || '',
        timezone: existingSchedule.timezone || 'America/New_York',
        targetType: existingSchedule.targetType || 'command',
        targetCode: existingSchedule.targetCode || '',
        input: existingSchedule.input || {},
        retryPolicyName: existingSchedule.retryPolicyName || 'default',
        enabled: existingSchedule.enabled ?? true,
        startDate: existingSchedule.startDate || '',
        endDate: existingSchedule.endDate || '',
        actionAfterCompletion: existingSchedule.actionAfterCompletion || 'NONE',
        delegatedIdentityCode: existingSchedule.delegatedIdentityCode || '',
        captureResponse: existingSchedule.captureResponse ?? false
      })
    }
  }, [isEdit, existingSchedule, form])

  const allCommandTargets = useMemo(() => {
    if (!targetGroups) return []
    return targetGroups.flatMap(g =>
      g.commandTargets.map(ct => ({
        ...ct,
        lambdaTargetDisplayName: g.lambdaTargetDisplayName
      }))
    )
  }, [targetGroups])

  const targetOptions = useMemo(() =>
    allCommandTargets.map(t => ({
      label: `${t.lambdaTargetDisplayName} — ${t.displayName}`,
      value: t.code
    })),
  [allCommandTargets])

  const identityOptions = useMemo(() => {
    if (!delegatedIdentities) return []
    return delegatedIdentities
      .filter(di => di.enabled)
      .map(di => ({ label: `${di.displayName} (${di.code})`, value: di.code }))
  }, [delegatedIdentities])

  const targetCode = form.watch('targetCode')
  const timezone = form.watch('timezone')

  const selectedTarget = useMemo(() =>
    allCommandTargets.find(t => t.code === targetCode),
  [allCommandTargets, targetCode])

  useEffect(() => {
    if (!targetCode) return
    if (isEdit && !form.formState.isDirty) return
    form.setValue('input', {})
  }, [targetCode, form, isEdit])

  const handleFormSubmit = useCallback(async (formData) => {
    try {
      setSubmitError(null)
      setProcessing(true)
      if (isEdit) {
        await modifySchedule({ scheduleId: Number(scheduleId), ...formData })
      } else {
        await createSchedule(formData)
      }
      history.push(managedBase)
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save schedule')
    } finally {
      setProcessing(false)
    }
  }, [isEdit, scheduleId, createSchedule, modifySchedule, history, managedBase])

  const onSubmit = form.handleSubmit(handleFormSubmit)

  if ((isEdit && isLoadingSchedule) || isLoadingTargets) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ width: '100%', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>{isEdit ? 'Edit Schedule' : 'New Schedule'}</h2>
            <FlexActions style={{ flex: 1 }}>
              <SydButton size='sm' variant='ghost' onClick={() => history.push(managedBase)}>
                Cancel
              </SydButton>
            </FlexActions>
          </FlexRow>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='name'
                  rules={{
                    required: 'Name is required',
                    maxLength: { value: 200, message: 'Max 200 characters' }
                  }}
                  render={(f) => (
                    <SydLabel label='Name' required error={hookFormErrorAdapter(form, f.fieldState)}>
                      <SydInput {...f.field} placeholder='Schedule name' />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='code'
                  rules={{
                    required: 'Code is required',
                    maxLength: { value: 96, message: 'Max 96 characters' },
                    pattern: {
                      value: CODE_PATTERN,
                      message: 'Lowercase letters, numbers, hyphens. Must start with letter or number.'
                    }
                  }}
                  render={(f) => (
                    <SydLabel
                      label='Code'
                      required
                      helperText='Lowercase letters, numbers, hyphens only.'
                      error={hookFormErrorAdapter(form, f.fieldState)}
                    >
                      <SydInput
                        {...f.field}
                        placeholder='e.g. daily-portfolio-sync'
                        disabled={isEdit}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='description'
                  render={(f) => (
                    <SydLabel label='Description'>
                      <SydInput {...f.field} placeholder='Optional description' />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='expression'
                  rules={{ required: 'Expression is required' }}
                  render={(f) => (
                    <CronBuilder
                      value={f.field.value}
                      timezone={timezone}
                      onChange={f.field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={form.control}
                  name='timezone'
                  render={(f) => (
                    <SydLabel label='Timezone'>
                      <SydSelect
                        options={TIMEZONE_OPTIONS}
                        value={f.field.value}
                        onChange={(e) => f.field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={form.control}
                  name='retryPolicyName'
                  render={(f) => (
                    <SydLabel label='Retry Policy'>
                      <SydSelect
                        options={RETRY_POLICIES}
                        value={f.field.value}
                        onChange={(e) => f.field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={form.control}
                  name='startDate'
                  render={(f) => (
                    <SydLabel label='Start Date'>
                      <SydInput type='date' {...f.field} />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={form.control}
                  name='endDate'
                  render={(f) => (
                    <SydLabel label='End Date'>
                      <SydInput type='date' {...f.field} />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='actionAfterCompletion'
                  render={(f) => (
                    <SydLabel label='Action After Completion'>
                      <SydSelect
                        options={ACTION_OPTIONS}
                        value={f.field.value}
                        onChange={(e) => f.field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='delegatedIdentityCode'
                  render={(f) => (
                    <SydLabel label='Run As (Delegated Identity)' helperText='The user identity this schedule assumes when it fires. Leave empty for system-level (firm only).'>
                      <SydSelect
                        options={[{ label: 'None (system)', value: '' }, ...identityOptions]}
                        value={f.field.value}
                        onChange={(e) => f.field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='captureResponse'
                  render={(f) => (
                    <SydLabel label='Diagnostics' helperText='When enabled, the downstream Lambda response is persisted with each invocation record.'>
                      <SydCheckbox
                        label='Capture Response'
                        value={f.field.value}
                        onChange={(_, checked) => f.field.onChange(checked)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Controller
                  control={form.control}
                  name='targetCode'
                  rules={{ required: 'Target is required' }}
                  render={(f) => (
                    <SydLabel label='Target' required error={hookFormErrorAdapter(form, f.fieldState)}>
                      <SydSelect
                        options={[{ label: 'Select a target...', value: '' }, ...targetOptions]}
                        value={f.field.value}
                        onChange={(e) => f.field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
                {selectedTarget?.description && (
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>
                    {selectedTarget.description}
                  </div>
                )}
              </Grid>
              {selectedTarget && (
                <Grid item xs={12}>
                  <Controller
                    control={form.control}
                    name='input'
                    render={(f) => (
                      <DynamicParameterFields
                        target={selectedTarget}
                        input={f.field.value}
                        onChange={f.field.onChange}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          {submitError ? <div style={{ color: 'red', marginBottom: 8 }}>{submitError}</div> : null}
          <SydButton
            variant='primary'
            processing={processing}
            disabled={!form.formState.isValid || processing}
            onClick={onSubmit}
          >
            {isEdit ? 'Save Changes' : 'Create Schedule'}
          </SydButton>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default ScheduleForm
